// ============================================================
// MELEE REACH - strict, footprint to footprint (map-display rulings,
// 2026-10-07; plan map-display, Track D).
//
// A melee strike reaches across the gap between the two bodies: the ground
// distance less both footprints (a figure's footprint is its size, through
// `tokenForSize`), and up or down the gap between the two bodies' heights -
// one on a roof is not in reach from below. A weapon reaches DEFAULT_REACH_M
// unless its `reach` says more. Only the map decides it: with positions off,
// or the map not allowed to decide, there is no gap and nothing is refused.
//
// Pure. Zero React dependency.
// ============================================================
import { tokenHeight, type Token } from './tokens';

/** How far a melee weapon reaches when it does not say: one metre. */
export const DEFAULT_REACH_M = 1;

type Body = Pick<Token, 'x' | 'z' | 'elevation' | 'footprint' | 'eyeHeight'> & { height?: number };

/** The gap a strike must cross between two figures, in metres; 0 when their bodies meet. */
export function reachGap(a: Body, b: Body): number {
  const across = Math.max(0, Math.hypot(a.x - b.x, a.z - b.z) - a.footprint - b.footprint);
  const up = Math.max(0, b.elevation - (a.elevation + tokenHeight(a)), a.elevation - (b.elevation + tokenHeight(b)));
  return Math.hypot(across, up);
}

/** The Reach field both weapon editors draw: its limits, its hint, its test id. */
export const REACH_FIELD = {
  title: 'How far it strikes, footprint to footprint. Only the map holds you to it.',
  input: { min: 0.5, max: 20, step: 0.5, float: true, 'data-testid': 'weapon-reach' },
} as const;

/** How far this weapon reaches, in metres. */
export const weaponReach = (weapon: { reach?: number } | null | undefined) =>
  weapon?.reach && weapon.reach > 0 ? weapon.reach : DEFAULT_REACH_M;

/** The refusal, when the map puts the target further than the weapon reaches; null otherwise (and with no map). */
export function reachReason(gap: number | null | undefined, reach: number): string | null {
  if (gap === null || gap === undefined || gap <= reach + 1e-6) return null;
  return `Out of reach: the target is ${gap.toFixed(1)} m away, and the weapon reaches ${reach} m.`;
}

/** In melee with each other: within a strike, height and all. The gang-up and leaving melee both read this one. */
export function inMelee(a: Body, b: Body, reach = DEFAULT_REACH_M): boolean {
  return reachGap(a, b) <= reach + 1e-6;
}
